import { ChartOptions } from "chart.js";
import { FC, memo, useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { useRtl } from "@ryde/redux";

import { IMultiSeriesContent } from "../../dynamic-dashboard.models";
import { DASHBOARD_CHART_BASE_OPTIONS } from "./dashboard-chart.constants";
import {
  dashboardCategoryAxis,
  dashboardValueAxis,
  defaultChartValueFormatter,
} from "./dashboard-chart.utils";
import { MultiSeriesChartConstants } from "./dashboard-multi-series-bar.chart.constants";

interface IDashboardMultiSeriesBarChartProps {
  multiSeries?: IMultiSeriesContent;
  metricLabel: string;
  dimensionLabel: string;
  /** Stack the series into a single bar (vs. clustered side-by-side). */
  stacked?: boolean;
  /** Formats the value in the tooltip; defaults to a localized number. */
  valueFormatter?: (value: number) => string;
}

/**
 * Dashboard clustered / stacked bar chart per the Figma "Rides cost per unit":
 * two (or more) series per category with a legend, in the brand purple family.
 * `stacked` toggles between a single stacked bar and side-by-side clustered bars.
 */
export const DashboardMultiSeriesBarChart: FC<IDashboardMultiSeriesBarChartProps> =
  memo(
    ({
      multiSeries,
      metricLabel,
      dimensionLabel,
      stacked = false,
      valueFormatter,
    }) => {
      const { isRtl } = useRtl();
      const formatValue = useMemo(
        () => valueFormatter ?? defaultChartValueFormatter,
        [valueFormatter],
      );
      const data = useMemo(() => {
        const categories = multiSeries?.categories ?? [];
        const series = multiSeries?.series ?? [];
        return {
          labels: categories,
          datasets: series.map((entry, index) => ({
            label: entry.label,
            data: entry.values,
            backgroundColor:
              MultiSeriesChartConstants.seriesColors[
                index % MultiSeriesChartConstants.seriesColors.length
              ],
            borderRadius: 4,
            maxBarThickness: MultiSeriesChartConstants.maxBarThickness,
            stack: stacked ? MultiSeriesChartConstants.stackId : undefined,
          })),
        };
      }, [multiSeries, stacked]);

      const options: ChartOptions<"bar"> = useMemo(
        () => ({
          ...DASHBOARD_CHART_BASE_OPTIONS,
          plugins: {
            legend: {
              display: true,
              position: "top",
              align: isRtl ? "end" : "start",
              rtl: isRtl,
              labels: { boxWidth: 12, font: { size: 11 }, usePointStyle: true },
            },
            tooltip: {
              rtl: isRtl,
              callbacks: {
                label: item =>
                  `${item.dataset.label}: ${formatValue(Number(item.raw))}`,
              },
            },
            datalabels: { display: false },
          },
          scales: {
            x: {
              ...dashboardCategoryAxis(dimensionLabel, {
                tickFontSize: MultiSeriesChartConstants.categoryTickFontSize,
                isRtl,
              }),
              stacked,
            },
            y: { ...dashboardValueAxis(metricLabel, { isRtl }), stacked },
          },
        }),
        [metricLabel, dimensionLabel, stacked, isRtl, formatValue],
      );

      return <Bar data={data} options={options} />;
    },
  );

DashboardMultiSeriesBarChart.displayName = "DashboardMultiSeriesBarChart";
